/**
 * Invoice PDF Generator Utility
 * Extracted from Invoices.js for better maintainability
 */
import jsPDF from 'jspdf';
import { formatDate } from './currency';

// PDF Theme Configurations
export const PDF_THEMES = {
  sage_cream: {
    primary: [107, 142, 107], dark: [54, 83, 54], leaf: [85, 130, 85],
    bg: [253, 251, 247], muted: [120, 113, 108], accent: [180, 140, 80],
    tableHeader: [245, 243, 239]
  },
  navy_gold: {
    primary: [27, 42, 74], dark: [15, 26, 48], leaf: [40, 60, 100],
    bg: [248, 246, 240], muted: [90, 101, 120], accent: [196, 151, 59],
    tableHeader: [235, 238, 245]
  },
  classic_bw: {
    primary: [26, 26, 26], dark: [0, 0, 0], leaf: [60, 60, 60],
    bg: [255, 255, 255], muted: [119, 119, 119], accent: [85, 85, 85],
    tableHeader: [240, 240, 240]
  },
  burgundy_ivory: {
    primary: [107, 45, 62], dark: [74, 30, 43], leaf: [130, 60, 80],
    bg: [255, 248, 240], muted: [139, 125, 107], accent: [139, 105, 20],
    tableHeader: [248, 240, 235]
  },
  teal_silver: {
    primary: [26, 107, 107], dark: [14, 77, 77], leaf: [40, 120, 120],
    bg: [240, 248, 248], muted: [107, 128, 128], accent: [139, 139, 139],
    tableHeader: [230, 242, 242]
  }
};

/**
 * Load image from URL and convert to base64 data URL
 */
export const loadImageAsDataUrl = async (url) => {
  if (!url) return null;
  
  try {
    // Rewrite stored URLs to use current backend (preview hostnames change between forks)
    let fetchUrl = url;
    const settingsImagePath = '/api/invoices/settings/image/';
    const pathIndex = url.indexOf(settingsImagePath);
    if (pathIndex !== -1) {
      fetchUrl = `${process.env.REACT_APP_BACKEND_URL}${url.substring(pathIndex)}`;
    }
    
    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error('Failed to fetch image');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.log('Image load failed:', url, e);
    return null;
  }
};

/**
 * Detect image format from data URL for jsPDF
 */
export const getImageFormat = (dataUrl) => {
  if (!dataUrl) return 'PNG';
  if (dataUrl.includes('data:image/jpeg') || dataUrl.includes('data:image/jpg')) return 'JPEG';
  if (dataUrl.includes('data:image/png')) return 'PNG';
  if (dataUrl.includes('data:image/webp')) return 'WEBP';
  if (dataUrl.includes('data:image/gif')) return 'GIF';
  return 'PNG';
};

/**
 * Truncate text with ellipsis for PDF
 */
export const truncateTextForPdf = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Fix stale image URLs for display (old preview hostnames)
 */
export const fixImageUrl = (url) => {
  if (!url) return url;
  const settingsImagePath = '/api/invoices/settings/image/';
  const pathIndex = url.indexOf(settingsImagePath);
  if (pathIndex !== -1) {
    return `${process.env.REACT_APP_BACKEND_URL}${url.substring(pathIndex)}`;
  }
  return url;
};

/**
 * Draw border design on PDF
 */
export const drawBorderDesign = (doc, borderDesign, t, pageWidth, pageHeight) => {
  if (borderDesign === 'botanical') {
    doc.setDrawColor(...t.primary);
    doc.setLineWidth(1);
    doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
    doc.setLineWidth(0.3);
    doc.rect(13, 13, pageWidth - 26, pageHeight - 26);
    // Decorative leaf corners
    doc.setDrawColor(...t.leaf);
    doc.setLineWidth(0.8);
    // Top-left
    doc.ellipse(18, 22, 6, 12, 'S');
    doc.line(18, 10, 18, 34);
    doc.ellipse(28, 18, 5, 10, 'S');
    doc.line(28, 8, 28, 28);
    doc.setLineWidth(0.5);
    doc.line(10, 35, 35, 10);
    // Top-right
    doc.setLineWidth(0.8);
    doc.ellipse(pageWidth - 18, 22, 6, 12, 'S');
    doc.line(pageWidth - 18, 10, pageWidth - 18, 34);
    doc.ellipse(pageWidth - 28, 18, 5, 10, 'S');
    doc.line(pageWidth - 28, 8, pageWidth - 28, 28);
    doc.setLineWidth(0.5);
    doc.line(pageWidth - 10, 35, pageWidth - 35, 10);
    // Bottom-left
    doc.setLineWidth(0.8);
    doc.ellipse(18, pageHeight - 22, 6, 12, 'S');
    doc.line(18, pageHeight - 10, 18, pageHeight - 34);
    doc.ellipse(28, pageHeight - 18, 5, 10, 'S');
    doc.line(28, pageHeight - 8, 28, pageHeight - 28);
    doc.setLineWidth(0.5);
    doc.line(10, pageHeight - 35, 35, pageHeight - 10);
    // Bottom-right
    doc.setLineWidth(0.8);
    doc.ellipse(pageWidth - 18, pageHeight - 22, 6, 12, 'S');
    doc.line(pageWidth - 18, pageHeight - 10, pageWidth - 18, pageHeight - 34);
    doc.ellipse(pageWidth - 28, pageHeight - 18, 5, 10, 'S');
    doc.line(pageWidth - 28, pageHeight - 8, pageWidth - 28, pageHeight - 28);
    doc.setLineWidth(0.5);
    doc.line(pageWidth - 10, pageHeight - 35, pageWidth - 35, pageHeight - 10);
  } else if (borderDesign === 'double_line') {
    doc.setDrawColor(...t.primary);
    doc.setLineWidth(1.2);
    doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
    doc.setLineWidth(0.3);
    doc.rect(13, 13, pageWidth - 26, pageHeight - 26);
  } else if (borderDesign === 'ornamental') {
    doc.setDrawColor(...t.primary);
    doc.setLineWidth(0.6);
    doc.rect(12, 12, pageWidth - 24, pageHeight - 24);
    // Corner circles
    doc.setDrawColor(...t.accent);
    doc.setLineWidth(0.5);
    const cr = 4;
    doc.circle(16, 16, cr, 'S');
    doc.circle(pageWidth - 16, 16, cr, 'S');
    doc.circle(16, pageHeight - 16, cr, 'S');
    doc.circle(pageWidth - 16, pageHeight - 16, cr, 'S');
    // Dashed accent lines
    doc.setDrawColor(...t.accent);
    doc.setLineWidth(0.3);
    for (let x = 24; x < pageWidth - 24; x += 5) {
      doc.line(x, 12, Math.min(x + 3, pageWidth - 24), 12);
    }
    for (let x = 24; x < pageWidth - 24; x += 5) {
      doc.line(x, pageHeight - 12, Math.min(x + 3, pageWidth - 24), pageHeight - 12);
    }
    for (let y = 24; y < pageHeight - 24; y += 5) {
      doc.line(12, y, 12, Math.min(y + 3, pageHeight - 24));
    }
    for (let y = 24; y < pageHeight - 24; y += 5) {
      doc.line(pageWidth - 12, y, pageWidth - 12, Math.min(y + 3, pageHeight - 24));
    }
  } else if (borderDesign === 'minimal') {
    doc.setDrawColor(...t.primary);
    doc.setLineWidth(0.5);
    doc.rect(12, 12, pageWidth - 24, pageHeight - 24);
  }
  // 'none' - no border drawn
};

/**
 * Generate a single invoice PDF
 */
export const generateInvoicePDF = async (invoice, invoiceSettings, onSuccess) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Get selected theme and border from settings
  const colorTheme = invoiceSettings.pdfTemplate?.colorTheme || 'sage_cream';
  const borderDesign = invoiceSettings.pdfTemplate?.borderDesign || 'botanical';
  const t = PDF_THEMES[colorTheme] || PDF_THEMES.sage_cream;
  
  // Pre-load all images as data URLs
  const logoUrl = invoice.venueLogoUrl || invoiceSettings.companyDetails?.logo_url;
  const qrCodeUrl = invoiceSettings.paymentQrCodeUrl;
  const signatureUrl = invoiceSettings.signature?.url;
  
  const [logoDataUrl, qrDataUrl, signatureDataUrl] = await Promise.all([
    loadImageAsDataUrl(logoUrl),
    loadImageAsDataUrl(qrCodeUrl),
    loadImageAsDataUrl(signatureUrl)
  ]);
  
  // ===== BACKGROUND =====
  doc.setFillColor(...t.bg);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // ===== BORDER DESIGN =====
  drawBorderDesign(doc, borderDesign, t, pageWidth, pageHeight);
  
  // ===== HEADER SECTION =====
  const centerX = pageWidth / 2;
  const company = invoiceSettings.companyDetails || {};
  
  // --- LEFT SIDE: Logo + Company Details ---
  let logoEndX = 20;
  if (logoDataUrl) {
    try {
      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = logoDataUrl;
      });
      const maxW = 28, maxH = 22;
      const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
      const w = img.naturalWidth * ratio;
      const h = img.naturalHeight * ratio;
      const logoFormat = getImageFormat(logoDataUrl);
      doc.addImage(logoDataUrl, logoFormat, 20, 16, w, h);
      logoEndX = 20 + w + 3;
    } catch (e) {
      console.log('Logo add failed:', e);
    }
  }
  
  // Company Name
  let compY = 20;
  if (company.name) {
    doc.setFontSize(12);
    doc.setTextColor(...t.dark);
    doc.setFont(undefined, 'bold');
    doc.text(company.name.toUpperCase(), logoEndX, compY);
    compY += 5;
  }
  
  // Company details
  doc.setFontSize(7);
  doc.setTextColor(...t.muted);
  doc.setFont(undefined, 'normal');
  
  if (company.address) {
    const addrLines = doc.splitTextToSize(company.address, 55);
    doc.text(addrLines.slice(0, 2), logoEndX, compY);
    compY += addrLines.slice(0, 2).length * 3.5;
  }
  if (company.gstin) {
    doc.text(`GSTIN: ${company.gstin}`, logoEndX, compY);
    compY += 3.5;
  }
  if (company.phone) {
    doc.text(`Ph: ${company.phone}`, logoEndX, compY);
    compY += 3.5;
  }
  if (company.email) {
    doc.text(company.email, logoEndX, compY);
  }
  
  // --- RIGHT SIDE: Invoice title + number + date ---
  doc.setFontSize(9);
  doc.setTextColor(...t.muted);
  doc.setFont(undefined, 'normal');
  doc.text(invoice.invoiceNumber, pageWidth - 20, 18, { align: 'right' });
  
  doc.setFontSize(28);
  doc.setTextColor(...t.dark);
  doc.setFont(undefined, 'italic');
  doc.text('Invoice', pageWidth - 20, 30, { align: 'right' });
  
  doc.setFontSize(8);
  doc.setTextColor(...t.muted);
  doc.setFont(undefined, 'normal');
  doc.text(`ISSUE DATE: ${formatDate(invoice.invoiceDate)}`, pageWidth - 20, 40, { align: 'right' });
  
  // ===== INVOICE TO SECTION =====
  const headerEndY = 48;
  doc.setDrawColor(...t.primary);
  doc.setLineWidth(0.3);
  doc.line(20, headerEndY, pageWidth - 20, headerEndY);
  
  doc.setFontSize(10);
  doc.setTextColor(...t.dark);
  doc.setFont(undefined, 'bold');
  doc.text('INVOICE TO', 25, headerEndY + 8);
  
  // Customer details - Left column
  let custY = headerEndY + 15;
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...t.dark);
  doc.text(invoice.customerName.toUpperCase(), 25, custY);
  custY += 5;
  
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...t.muted);
  
  if (invoice.customerAddress) {
    const addrLines = doc.splitTextToSize(invoice.customerAddress, 70);
    doc.text(addrLines.slice(0, 2), 25, custY);
    custY += addrLines.slice(0, 2).length * 3.5;
  }
  if (invoice.customerPhone) {
    doc.text(`Ph: ${invoice.customerPhone}`, 25, custY);
    custY += 3.5;
  }
  if (invoice.customerEmail) {
    doc.text(invoice.customerEmail, 25, custY);
    custY += 3.5;
  }
  if (invoice.customerGstin) {
    doc.text(`GSTIN: ${invoice.customerGstin}`, 25, custY);
    custY += 3.5;
  }
  
  // Invoice meta - Right column
  let metaY = headerEndY + 15;
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...t.muted);
  if (invoice.venueName) {
    doc.text(`Venue: ${invoice.venueName}`, pageWidth - 25, metaY, { align: 'right' });
    metaY += 4;
  }
  if (invoice.dueDate) {
    doc.text(`Due: ${formatDate(invoice.dueDate)}`, pageWidth - 25, metaY, { align: 'right' });
    metaY += 4;
  }
  
  const invoiceToEndY = Math.max(custY + 3, metaY + 3);
  doc.setDrawColor(...t.primary);
  doc.line(20, invoiceToEndY, pageWidth - 20, invoiceToEndY);
  
  // ===== LINE ITEMS TABLE =====
  doc.setFillColor(...t.tableHeader);
  doc.rect(20, invoiceToEndY + 4, pageWidth - 40, 10, 'F');
  
  doc.setFontSize(9);
  doc.setTextColor(...t.dark);
  doc.setFont(undefined, 'bold');
  doc.text('#', 24, invoiceToEndY + 11);
  doc.text('DESCRIPTION', 32, invoiceToEndY + 11);
  doc.text('PRICE', 115, invoiceToEndY + 11);
  doc.text('QTY', 145, invoiceToEndY + 11);
  doc.text('TOTAL', pageWidth - 25, invoiceToEndY + 11, { align: 'right' });
  
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...t.muted);
  let tableY = invoiceToEndY + 22;
  
  (invoice.lineItems || []).forEach((item, idx) => {
    doc.text(String(idx + 1), 24, tableY);
    doc.text(truncateTextForPdf(item.service || 'Item', 48), 32, tableY);
    doc.text(`Rs. ${(item.unit_price || 0).toLocaleString('en-IN')}`, 115, tableY);
    doc.text(String(item.quantity || 1), 150, tableY);
    doc.text(`Rs. ${((item.unit_price || 0) * (item.quantity || 1)).toLocaleString('en-IN')}`, pageWidth - 25, tableY, { align: 'right' });
    tableY += 10;
  });
  
  doc.setDrawColor(...t.primary);
  doc.line(20, tableY + 2, pageWidth - 20, tableY + 2);
  
  // ===== PAYMENT INFO & TOTALS =====
  const paymentY = tableY + 15;
  const bankInfo = invoiceSettings.bankDetails || invoice.bankDetails || {};
  const bankMaxChars = 28;
  
  doc.setFontSize(9);
  doc.setTextColor(...t.dark);
  doc.setFont(undefined, 'bold');
  doc.text('PAYMENT INFO', 25, paymentY);
  
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...t.muted);
  doc.setFontSize(7);
  if (bankInfo.account_name) {
    doc.text(`ACCOUNT NAME: ${truncateTextForPdf(bankInfo.account_name, bankMaxChars)}`, 25, paymentY + 7);
  }
  if (bankInfo.account_number) {
    doc.text(`ACCOUNT NUMBER: ${truncateTextForPdf(bankInfo.account_number, bankMaxChars)}`, 25, paymentY + 12);
  }
  if (bankInfo.bank_name) {
    doc.text(`BANK: ${truncateTextForPdf(bankInfo.bank_name, bankMaxChars)}`, 25, paymentY + 17);
  }
  if (bankInfo.ifsc_code) {
    doc.text(`IFSC: ${truncateTextForPdf(bankInfo.ifsc_code, bankMaxChars)}`, 25, paymentY + 22);
  }
  if (bankInfo.upi_id) {
    doc.text(`UPI: ${truncateTextForPdf(bankInfo.upi_id, bankMaxChars)}`, 25, paymentY + 27);
  }
  
  // QR Code
  if (qrDataUrl) {
    try {
      const qrFormat = getImageFormat(qrDataUrl);
      doc.addImage(qrDataUrl, qrFormat, 82, paymentY + 2, 26, 26);
      doc.setFontSize(6);
      doc.setTextColor(...t.muted);
      doc.text('Scan to Pay', 95, paymentY + 30, { align: 'center' });
    } catch (e) {
      console.log('QR code add failed:', e);
    }
  }
  
  // Totals (Right Side)
  doc.setFontSize(9);
  doc.setTextColor(...t.muted);
  doc.setFont(undefined, 'normal');
  
  doc.text('TOTAL', 130, paymentY);
  doc.text(`Rs. ${(invoice.subtotal || 0).toLocaleString('en-IN')}`, pageWidth - 25, paymentY, { align: 'right' });
  
  let totY = paymentY + 10;
  
  if (invoice.discount_amount > 0) {
    doc.text(`DISCOUNT (${invoice.discountPercent || 0}%)`, 130, totY);
    doc.text(`-Rs. ${invoice.discount_amount.toLocaleString('en-IN')}`, pageWidth - 25, totY, { align: 'right' });
    totY += 10;
  }
  
  if (invoice.gstConfig?.enabled) {
    if (invoice.gstConfig.gst_type === 'igst') {
      doc.text(`TAX (${invoice.gstConfig.igst_rate}%)`, 130, totY);
      doc.text(`Rs. ${(invoice.igst || 0).toLocaleString('en-IN')}`, pageWidth - 25, totY, { align: 'right' });
    } else {
      doc.text(`CGST (${invoice.gstConfig.cgst_rate}%)`, 130, totY);
      doc.text(`Rs. ${(invoice.cgst || 0).toLocaleString('en-IN')}`, pageWidth - 25, totY, { align: 'right' });
      totY += 10;
      doc.text(`SGST (${invoice.gstConfig.sgst_rate}%)`, 130, totY);
      doc.text(`Rs. ${(invoice.sgst || 0).toLocaleString('en-IN')}`, pageWidth - 25, totY, { align: 'right' });
    }
    totY += 10;
  }
  
  // Round Off (if applicable)
  if (invoice.round_off_amount !== undefined && invoice.round_off_amount !== 0) {
    doc.text('ROUND OFF', 130, totY);
    const roundOffDisplay = invoice.round_off_amount > 0 
      ? `+Rs. ${invoice.round_off_amount.toFixed(2)}` 
      : `-Rs. ${Math.abs(invoice.round_off_amount).toFixed(2)}`;
    doc.text(roundOffDisplay, pageWidth - 25, totY, { align: 'right' });
    totY += 10;
  }
  
  // Grand Total Line
  doc.setDrawColor(...t.primary);
  doc.line(125, totY + 2, pageWidth - 20, totY + 2);
  
  // Grand Total
  totY += 12;
  doc.setFontSize(11);
  doc.setTextColor(...t.dark);
  doc.setFont(undefined, 'bold');
  doc.text('GRAND TOTAL:', 125, totY);
  doc.text(`Rs. ${(invoice.grand_total || 0).toLocaleString('en-IN')}`, pageWidth - 25, totY, { align: 'right' });
  
  // ===== TERMS & CONDITIONS =====
  const termsY = Math.max(totY + 20, paymentY + 75);
  doc.setDrawColor(...t.primary);
  doc.line(20, termsY, pageWidth - 20, termsY);
  
  if (invoice.termsConditions) {
    doc.setFontSize(8);
    doc.setTextColor(...t.dark);
    doc.setFont(undefined, 'bold');
    doc.text('Terms & Conditions', 25, termsY + 8);
    
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...t.muted);
    doc.setFontSize(7);
    const splitTerms = doc.splitTextToSize(invoice.termsConditions, 80);
    doc.text(splitTerms.slice(0, 5), 25, termsY + 14);
  }

  if (invoice.notes) {
    const notesX = pageWidth / 2 + 5;
    const notesMaxWidth = 70;
    doc.setFontSize(8);
    doc.setTextColor(...t.dark);
    doc.setFont(undefined, 'bold');
    doc.text('Notes', notesX, termsY + 8);
    
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...t.muted);
    doc.setFontSize(7);
    const splitNotes = doc.splitTextToSize(invoice.notes, notesMaxWidth);
    doc.text(splitNotes.slice(0, 4), notesX, termsY + 14);
  }
  
  // ===== SIGNATURE =====
  const signature = invoiceSettings.signature;
  if (signature?.showOnInvoice !== false && (signatureDataUrl || signature?.name)) {
    const sigX = pageWidth - 80;
    const sigY = pageHeight - 55;
    
    if (signatureDataUrl) {
      try {
        const sigImg = new Image();
        sigImg.src = signatureDataUrl;
        const sigMaxW = 40, sigMaxH = 18;
        const sigRatio = Math.min(sigMaxW / (sigImg.naturalWidth || sigMaxW), sigMaxH / (sigImg.naturalHeight || sigMaxH));
        const sigW = (sigImg.naturalWidth || sigMaxW) * sigRatio;
        const sigH = (sigImg.naturalHeight || sigMaxH) * sigRatio;
        const sigFormat = getImageFormat(signatureDataUrl);
        doc.addImage(signatureDataUrl, sigFormat, sigX + 5, sigY, sigW, sigH);
      } catch (e) {
        console.log('Signature add failed:', e);
      }
    }
    
    doc.setDrawColor(...t.dark);
    doc.setLineWidth(0.3);
    doc.line(sigX, sigY + 16, sigX + 45, sigY + 16);
    
    doc.setFontSize(8);
    doc.setTextColor(...t.dark);
    doc.setFont(undefined, 'normal');
    if (signature?.name) {
      doc.text(signature.name, sigX + 22, sigY + 22, { align: 'center' });
    }
    doc.setFontSize(7);
    doc.setTextColor(...t.muted);
    if (signature?.designation) {
      doc.text(signature.designation, sigX + 22, sigY + 27, { align: 'center' });
    }
  }
  
  // Thank you message
  doc.setFontSize(9);
  doc.setTextColor(...t.primary);
  doc.setFont(undefined, 'italic');
  doc.text('Thank you for your business!', centerX, pageHeight - 18, { align: 'center' });
  
  doc.save(`Invoice_${invoice.invoiceNumber}.pdf`);
  if (onSuccess) onSuccess();
};

/**
 * Render a single invoice page into an existing jsPDF doc (for bulk PDF)
 */
export const renderInvoicePage = async (doc, invoice, invoiceSettings) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  const colorTheme = invoiceSettings.pdfTemplate?.colorTheme || 'sage_cream';
  const borderDesign = invoiceSettings.pdfTemplate?.borderDesign || 'botanical';
  const t = PDF_THEMES[colorTheme] || PDF_THEMES.sage_cream;
  
  const logoUrl = invoice.venueLogoUrl || invoiceSettings.companyDetails?.logo_url;
  const qrCodeUrl = invoiceSettings.paymentQrCodeUrl;
  const signatureUrl = invoiceSettings.signature?.url;
  
  const [logoDataUrl, qrDataUrl, signatureDataUrl] = await Promise.all([
    loadImageAsDataUrl(logoUrl),
    loadImageAsDataUrl(qrCodeUrl),
    loadImageAsDataUrl(signatureUrl)
  ]);
  
  // Background
  doc.setFillColor(...t.bg);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Border
  drawBorderDesign(doc, borderDesign, t, pageWidth, pageHeight);
  
  // Header
  const centerX = pageWidth / 2;
  const company = invoiceSettings.companyDetails || {};
  let logoEndX = 20;
  
  if (logoDataUrl) { 
    try { 
      const lI = await new Promise((res, rej) => { const img = new Image(); img.onload = () => res(img); img.onerror = rej; img.src = logoDataUrl; });
      const lMW = 28, lMH = 22; 
      const lR = Math.min(lMW/lI.naturalWidth, lMH/lI.naturalHeight); 
      const lW = lI.naturalWidth*lR;
      const logoFormat = getImageFormat(logoDataUrl);
      doc.addImage(logoDataUrl, logoFormat, 20, 16, lW, lI.naturalHeight*lR); 
      logoEndX = 20 + lW + 3; 
    } catch(e) { /* image skip */ } 
  }
  
  let compY = 20;
  if (company.name) { doc.setFontSize(12); doc.setTextColor(...t.dark); doc.setFont(undefined, 'bold'); doc.text(company.name.toUpperCase(), logoEndX, compY); compY += 5; }
  doc.setFontSize(7); doc.setTextColor(...t.muted); doc.setFont(undefined, 'normal');
  if (company.address) { const al = doc.splitTextToSize(company.address, 55); doc.text(al.slice(0,2), logoEndX, compY); compY += al.slice(0,2).length * 3.5; }
  if (company.gstin) { doc.text(`GSTIN: ${company.gstin}`, logoEndX, compY); compY += 3.5; }
  if (company.phone) { doc.text(`Ph: ${company.phone}`, logoEndX, compY); compY += 3.5; }
  if (company.email) { doc.text(company.email, logoEndX, compY); }
  
  doc.setFontSize(9); doc.setTextColor(...t.muted); doc.setFont(undefined, 'normal');
  doc.text(invoice.invoiceNumber, pageWidth-20, 18, { align: 'right' });
  doc.setFontSize(28); doc.setTextColor(...t.dark); doc.setFont(undefined, 'italic');
  doc.text('Invoice', pageWidth-20, 30, { align: 'right' });
  doc.setFontSize(8); doc.setTextColor(...t.muted); doc.setFont(undefined, 'normal');
  doc.text(`ISSUE DATE: ${formatDate(invoice.invoiceDate)}`, pageWidth-20, 40, { align: 'right' });
  
  // Invoice To
  const headerEndY = 48;
  doc.setDrawColor(...t.primary); doc.setLineWidth(0.3); doc.line(20, headerEndY, pageWidth-20, headerEndY);
  doc.setFontSize(10); doc.setTextColor(...t.dark); doc.setFont(undefined, 'bold'); doc.text('INVOICE TO', 25, headerEndY+8);
  let custY = headerEndY+15;
  doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(...t.dark);
  doc.text(invoice.customerName.toUpperCase(), 25, custY); custY += 5;
  doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(...t.muted);
  if (invoice.customerAddress) { const al = doc.splitTextToSize(invoice.customerAddress, 70); doc.text(al.slice(0,2), 25, custY); custY += al.slice(0,2).length * 3.5; }
  if (invoice.customerPhone) { doc.text(`Ph: ${invoice.customerPhone}`, 25, custY); custY += 3.5; }
  if (invoice.customerEmail) { doc.text(invoice.customerEmail, 25, custY); custY += 3.5; }
  if (invoice.customerGstin) { doc.text(`GSTIN: ${invoice.customerGstin}`, 25, custY); custY += 3.5; }
  
  let metaY = headerEndY+15; doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(...t.muted);
  if (invoice.venueName) { doc.text(`Venue: ${invoice.venueName}`, pageWidth-25, metaY, { align: 'right' }); metaY += 4; }
  if (invoice.dueDate) { doc.text(`Due: ${formatDate(invoice.dueDate)}`, pageWidth-25, metaY, { align: 'right' }); metaY += 4; }
  
  const invoiceToEndY = Math.max(custY+3, metaY+3);
  doc.setDrawColor(...t.primary); doc.line(20, invoiceToEndY, pageWidth-20, invoiceToEndY);
  
  // Table
  doc.setFillColor(...t.tableHeader); doc.rect(20, invoiceToEndY+4, pageWidth-40, 10, 'F');
  doc.setFontSize(9); doc.setTextColor(...t.dark); doc.setFont(undefined, 'bold');
  doc.text('#', 24, invoiceToEndY+11); doc.text('DESCRIPTION', 32, invoiceToEndY+11); doc.text('PRICE', 115, invoiceToEndY+11);
  doc.text('QTY', 145, invoiceToEndY+11); doc.text('TOTAL', pageWidth-25, invoiceToEndY+11, { align: 'right' });
  doc.setFont(undefined, 'normal'); doc.setTextColor(...t.muted);
  let tableY = invoiceToEndY+22;
  (invoice.lineItems || []).forEach((item, idx) => {
    doc.text(String(idx + 1), 24, tableY);
    doc.text(truncateTextForPdf(item.service || 'Item', 48), 32, tableY);
    doc.text(`Rs. ${(item.unit_price||0).toLocaleString('en-IN')}`, 115, tableY);
    doc.text(String(item.quantity||1), 150, tableY);
    doc.text(`Rs. ${((item.unit_price||0)*(item.quantity||1)).toLocaleString('en-IN')}`, pageWidth-25, tableY, { align: 'right' });
    tableY += 10;
  });
  doc.setDrawColor(...t.primary); doc.line(20, tableY+2, pageWidth-20, tableY+2);
  
  // Payment + Totals
  const paymentY = tableY+15;
  const bankInfo = invoiceSettings.bankDetails || invoice.bankDetails || {};
  const bankMaxChars = 28;
  doc.setFontSize(9); doc.setTextColor(...t.dark); doc.setFont(undefined, 'bold'); doc.text('PAYMENT INFO', 25, paymentY);
  doc.setFont(undefined, 'normal'); doc.setTextColor(...t.muted); doc.setFontSize(7);
  if (bankInfo.account_name) doc.text(`ACCOUNT NAME: ${truncateTextForPdf(bankInfo.account_name, bankMaxChars)}`, 25, paymentY+7);
  if (bankInfo.account_number) doc.text(`ACCOUNT NUMBER: ${truncateTextForPdf(bankInfo.account_number, bankMaxChars)}`, 25, paymentY+12);
  if (bankInfo.bank_name) doc.text(`BANK: ${truncateTextForPdf(bankInfo.bank_name, bankMaxChars)}`, 25, paymentY+17);
  if (bankInfo.ifsc_code) doc.text(`IFSC: ${truncateTextForPdf(bankInfo.ifsc_code, bankMaxChars)}`, 25, paymentY+22);
  if (bankInfo.upi_id) doc.text(`UPI: ${truncateTextForPdf(bankInfo.upi_id, bankMaxChars)}`, 25, paymentY+27);
  if (qrDataUrl) { try { const qrFormat = getImageFormat(qrDataUrl); doc.addImage(qrDataUrl, qrFormat, 82, paymentY+2, 26, 26); doc.setFontSize(6); doc.setTextColor(...t.muted); doc.text('Scan to Pay', 95, paymentY+30, { align: 'center' }); } catch(e) { /* image skip */ } }
  
  doc.setFontSize(9); doc.setTextColor(...t.muted); doc.setFont(undefined, 'normal');
  doc.text('TOTAL', 130, paymentY); doc.text(`Rs. ${(invoice.subtotal||0).toLocaleString('en-IN')}`, pageWidth-25, paymentY, { align: 'right' });
  let totY = paymentY+10;
  if (invoice.discount_amount > 0) { doc.text(`DISCOUNT (${invoice.discountPercent||0}%)`, 130, totY); doc.text(`-Rs. ${invoice.discount_amount.toLocaleString('en-IN')}`, pageWidth-25, totY, { align: 'right' }); totY += 10; }
  if (invoice.gstConfig?.enabled) {
    if (invoice.gstConfig.gst_type === 'igst') { doc.text(`TAX (${invoice.gstConfig.igst_rate}%)`, 130, totY); doc.text(`Rs. ${(invoice.igst||0).toLocaleString('en-IN')}`, pageWidth-25, totY, { align: 'right' }); }
    else { doc.text(`CGST (${invoice.gstConfig.cgst_rate}%)`, 130, totY); doc.text(`Rs. ${(invoice.cgst||0).toLocaleString('en-IN')}`, pageWidth-25, totY, { align: 'right' }); totY += 10;
      doc.text(`SGST (${invoice.gstConfig.sgst_rate}%)`, 130, totY); doc.text(`Rs. ${(invoice.sgst||0).toLocaleString('en-IN')}`, pageWidth-25, totY, { align: 'right' }); }
    totY += 10;
  }
  // Round Off
  if (invoice.round_off_amount !== undefined && invoice.round_off_amount !== 0) {
    doc.text('ROUND OFF', 130, totY);
    const roundOffDisplay = invoice.round_off_amount > 0 ? `+Rs. ${invoice.round_off_amount.toFixed(2)}` : `-Rs. ${Math.abs(invoice.round_off_amount).toFixed(2)}`;
    doc.text(roundOffDisplay, pageWidth-25, totY, { align: 'right' }); totY += 10;
  }
  doc.setDrawColor(...t.primary); doc.line(125, totY+2, pageWidth-20, totY+2); totY += 12;
  doc.setFontSize(11); doc.setTextColor(...t.dark); doc.setFont(undefined, 'bold');
  doc.text('GRAND TOTAL:', 125, totY); doc.text(`Rs. ${(invoice.grand_total||0).toLocaleString('en-IN')}`, pageWidth-25, totY, { align: 'right' });
  
  // Terms
  const termsY = Math.max(totY+20, paymentY+75);
  doc.setDrawColor(...t.primary); doc.line(20, termsY, pageWidth-20, termsY);
  if (invoice.termsConditions) { doc.setFontSize(8); doc.setTextColor(...t.dark); doc.setFont(undefined, 'bold'); doc.text('Terms & Conditions', 25, termsY+8);
    doc.setFont(undefined, 'normal'); doc.setTextColor(...t.muted); doc.setFontSize(7); doc.text(doc.splitTextToSize(invoice.termsConditions, 80).slice(0,5), 25, termsY+14); }
  if (invoice.notes) { const notesX = pageWidth / 2 + 5; const notesMaxWidth = 70; doc.setFontSize(8); doc.setTextColor(...t.dark); doc.setFont(undefined, 'bold'); doc.text('Notes', notesX, termsY+8);
    doc.setFont(undefined, 'normal'); doc.setTextColor(...t.muted); doc.setFontSize(7); doc.text(doc.splitTextToSize(invoice.notes, notesMaxWidth).slice(0,4), notesX, termsY+14); }
  
  // Signature
  const sig = invoiceSettings.signature;
  if (sig?.showOnInvoice !== false && (signatureDataUrl || sig?.name)) { const sX = pageWidth-80, sY = pageHeight-55;
    if (signatureDataUrl) { try { const sI = new Image(); sI.src = signatureDataUrl; const sMW = 40, sMH = 18; const sR = Math.min(sMW/(sI.naturalWidth||sMW), sMH/(sI.naturalHeight||sMH)); const sigFormat = getImageFormat(signatureDataUrl); doc.addImage(signatureDataUrl, sigFormat, sX+5, sY, (sI.naturalWidth||sMW)*sR, (sI.naturalHeight||sMH)*sR); } catch(e) { /* image skip */ } }
    doc.setDrawColor(...t.dark); doc.setLineWidth(0.3); doc.line(sX, sY+16, sX+45, sY+16);
    doc.setFontSize(8); doc.setTextColor(...t.dark); doc.setFont(undefined, 'normal');
    if (sig?.name) doc.text(sig.name, sX+22, sY+22, { align: 'center' });
    doc.setFontSize(7); doc.setTextColor(...t.muted);
    if (sig?.designation) doc.text(sig.designation, sX+22, sY+27, { align: 'center' });
  }
  doc.setFontSize(9); doc.setTextColor(...t.primary); doc.setFont(undefined, 'italic');
  doc.text('Thank you for your business!', centerX, pageHeight-18, { align: 'center' });
};

/**
 * Generate bulk PDF with multiple invoices
 */
export const generateBulkPDF = async (invoices, invoiceSettings, onProgress, onSuccess, onError) => {
  if (invoices.length === 0) {
    if (onError) onError('No invoices selected');
    return;
  }
  
  const doc = new jsPDF();
  
  for (let i = 0; i < invoices.length; i++) {
    if (i > 0) doc.addPage();
    await renderInvoicePage(doc, invoices[i], invoiceSettings);
    if (onProgress) onProgress(i + 1, invoices.length);
  }
  
  const filename = invoices.length === 1 
    ? `Invoice_${invoices[0].invoiceNumber}.pdf`
    : `Invoices_Bulk_${new Date().toISOString().split('T')[0]}.pdf`;
  
  doc.save(filename);
  if (onSuccess) onSuccess();
};
